import { Accordion, useAccordionItemContext } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ExperienceConfigurationKeys,
  TenantSettingsConfigSchema,
} from '@monetize/types/experience-config';
import { useEffect } from 'react';
import {
  Control,
  Controller,
  UseFormHandleSubmit,
  useForm,
} from 'react-hook-form';
import { BsFiletypeJson } from 'react-icons/bs';
import z from 'zod';
import {
  useGetExperienceConfiguration,
  useUpdateExperienceConfiguration,
} from '../../../api/experienceConfigurationService';
import {
  AsyncStatusIndicator,
  MAccordion,
  MAccordionCustomButton,
  MAccordionItem,
  MBox,
  MIcon,
  MSkeleton,
  MSwitch,
  MText,
  MVStack,
} from '../../../components/Monetize';
import { UserRoleMultiSelect } from '../../../components/Monetize/Dropdowns/UserRoleMultiSelect';
import { useAccordionControl } from '../../../hooks';
import { useAsyncStatus } from '../../../hooks/useAsyncStatus';
import { useSettingsSectionHighlight } from '../../../hooks/useSettingsSectionHighlight';
import { useFlags } from '../../../services/launchDarkly';
import { logger } from '../../../services/logger';
import { SettingsControl } from '../../Settings/components/SettingsControl';
import { TENANT_SETTINGS_SECTIONS } from '../../Settings/SideNavBar/settingsNavSections';

const FormSchema = z.object({
  tenantSettings: TenantSettingsConfigSchema,
});
type Form = z.infer<typeof FormSchema>;

interface DebugModeSectionProps {
  control: Control<any>;
  isLoading: boolean;
  debugModeStatus: ReturnType<typeof useAsyncStatus>;
  onSubmit: (formValues: Form) => Promise<void>;
  handleSubmit: UseFormHandleSubmit<Form>;
}

// v3 dropped v2's render-prop children (`{({isExpanded}) => ...}`) on
// AccordionItem - the isExpanded-dependent content moves into its own
// child component reading `useAccordionItemContext()`.
const DebugModeSection = ({
  control,
  isLoading,
  debugModeStatus,
  onSubmit,
  handleSubmit,
}: DebugModeSectionProps) => {
  const { expanded: isExpanded } = useAccordionItemContext();

  return (
    <>
      <MAccordionCustomButton
        isExpanded={isExpanded}
        label={TENANT_SETTINGS_SECTIONS['additional'].label}
      />
      <Accordion.ItemContent mx={6}>
        <Accordion.ItemBody>
          <Controller
            name={`tenantSettings.debugModeRoles`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <SettingsControl
                name="tenantSettings.debugModeRoles"
                heading="Debug Mode"
                subheading={
                  <MText>
                    Enabling debug mode provides a way to easily inspect the
                    JSON data for records by clicking the JSON icon{' '}
                    <MIcon as={BsFiletypeJson} color="tPurple.dark" /> on record
                    pages.
                  </MText>
                }
                fetchLoading={isLoading}
                isDisabled={false}
                control={
                  <MBox minW="fit-content">
                    <UserRoleMultiSelect
                      value={value}
                      isCloseButtonLoading={
                        debugModeStatus.status === 'loading'
                      }
                      onChange={(newValue) => {
                        onChange(newValue);
                        debugModeStatus.track(() => handleSubmit(onSubmit)());
                      }}
                    />
                  </MBox>
                }
              />
            )}
          />
        </Accordion.ItemBody>
      </Accordion.ItemContent>
    </>
  );
};

interface LanguageLocaleSectionProps {
  control: Control<any>;
  isLoading: boolean;
  languageStatus: ReturnType<typeof useAsyncStatus>;
  onSubmit: (formValues: Form) => Promise<void>;
  handleSubmit: UseFormHandleSubmit<Form>;
}

const LanguageLocaleSection = ({
  control,
  isLoading,
  languageStatus,
  onSubmit,
  handleSubmit,
}: LanguageLocaleSectionProps) => {
  const { expanded: isExpanded } = useAccordionItemContext();

  return (
    <>
      <MAccordionCustomButton
        isExpanded={isExpanded}
        label={TENANT_SETTINGS_SECTIONS.languageLocale.label}
      />
      <Accordion.ItemContent mx={6}>
        <Accordion.ItemBody>
          <Controller
            name={`tenantSettings.enableQuoteOutputLanguageSelection`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <SettingsControl
                name="tenantSettings.enableQuoteOutputLanguageSelection"
                heading="Allow Output Document Language Selection"
                subheading={
                  <MText>
                    Allow sales reps to choose the output language for output
                    documents such as Quotes.
                  </MText>
                }
                fetchLoading={isLoading}
                isDisabled={languageStatus.status === 'loading'}
                control={
                  <MBox position="relative">
                    <MSwitch
                      isChecked={value}
                      isDisabled={languageStatus.status === 'loading'}
                      onChange={(e) => {
                        onChange(e.target.checked);
                        languageStatus.track(() => handleSubmit(onSubmit)());
                      }}
                    />
                    <AsyncStatusIndicator status={languageStatus.status} />
                  </MBox>
                }
              />
            )}
          />
        </Accordion.ItemBody>
      </Accordion.ItemContent>
    </>
  );
};

export const TenantAdditionalSettings = () => {
  const { data, isLoading } = useGetExperienceConfiguration();
  const { enableQuoteOutputLanguageSettings } = useFlags();
  const { accordionValue, handleAccordionChange, setAccordionValue } =
    useAccordionControl({
      itemCount: enableQuoteOutputLanguageSettings ? 2 : 1,
      defaultValue: ['0'],
    });

  const isAdditionalHighlighted = useSettingsSectionHighlight('additional');
  const isLanguageHighlighted = useSettingsSectionHighlight('languageLocale');

  useEffect(() => {
    if (isLanguageHighlighted) {
      setAccordionValue(['0', '1']);
    }
  }, [isLanguageHighlighted, setAccordionValue]);

  const debugModeStatus = useAsyncStatus();
  const languageStatus = useAsyncStatus();

  const { mutateAsync: doUpdateExperienceConfiguration } =
    useUpdateExperienceConfiguration({
      meta: { showErrorToast: true },
    });

  const { control, reset, handleSubmit } = useForm({
    mode: 'onChange',
    resolver: zodResolver(FormSchema),
    defaultValues: {
      tenantSettings: data?.tenantSettings || {
        debugModeRoles: [],
      },
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        tenantSettings: data.tenantSettings,
      });
    }
  }, [data, reset]);

  const onSubmit = async (formValues: Form) => {
    try {
      const promises: Promise<any>[] = [];
      promises.push(
        doUpdateExperienceConfiguration({
          key: ExperienceConfigurationKeys.tenantSettings,
          configuration: formValues.tenantSettings,
        }),
      );
      await Promise.all(promises);
    } catch (ex) {
      logger.warn('Error updating experience configuration', ex);
      throw ex;
    }
  };

  if (isLoading) {
    return (
      <MVStack maxW="600px" gap={4}>
        <MSkeleton height={50} width="100%" />
        <MSkeleton height={50} width="100%" />
        <MSkeleton height={50} width="100%" />
      </MVStack>
    );
  }

  return (
    <MAccordion
      multiple
      w="full"
      value={accordionValue}
      onValueChange={({ value }) => handleAccordionChange(value)}
    >
      <MAccordionItem
        value="0"
        id="additional"
        borderWidth="1px"
        borderRadius="md"
        borderColor={isAdditionalHighlighted ? 'tIndigo.base' : 'tGray.back'}
        bg={isAdditionalHighlighted ? 'tBlue.hover' : 'transparent'}
        transition="border-color 1.5s ease, background 1.5s ease"
        data-testid="default-msa-accordion"
      >
        <DebugModeSection
          control={control}
          isLoading={isLoading}
          debugModeStatus={debugModeStatus}
          onSubmit={onSubmit}
          handleSubmit={handleSubmit}
        />
      </MAccordionItem>
      {enableQuoteOutputLanguageSettings && (
        <MAccordionItem
          value="1"
          id="languageLocale"
          borderWidth="1px"
          borderRadius="md"
          borderColor={isLanguageHighlighted ? 'tIndigo.base' : 'tGray.back'}
          bg={isLanguageHighlighted ? 'tBlue.hover' : 'transparent'}
          transition="border-color 1.5s ease, background 1.5s ease"
          mt={4}
          data-testid="language-locale-settings-accordion"
        >
          <LanguageLocaleSection
            control={control}
            isLoading={isLoading}
            languageStatus={languageStatus}
            onSubmit={onSubmit}
            handleSubmit={handleSubmit}
          />
        </MAccordionItem>
      )}
    </MAccordion>
  );
};
